import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { JsonConvert } from 'json2typescript';
import { EMPTY, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { HrConfigurationService } from 'humanresources-common-shared';
import { LookupModel } from 'infrastructure-shared';
import { PerObjectDefinitionModel } from '../model/per-object-definition-model';
import { PerObjectDefinitionQueryModel } from '../model/per-object-definition-query-model';
import {
    PerObjectDefinitonQueryWithOrganizationDatesModel,
} from '../model/per-object-definiton-query-with-organization-dates-model';
import { OrganizationSchemaModel } from '../model/organization-schema-model';
import { EnumObject } from '../../../../../model/enum-object';
import { ObjectStatusConstraints } from '../../../../../model/object-status-constraints';
import { ObjectHierarchyQueryModel } from '../model/object-hierarchy-query-model';
import { PositionLookupModel } from '../model/position-lookup-model';
import { UiToastMessageService, UiTranslateLoaderService } from 'ui-shared';
import { OrganizationMoveModel } from '../../../model/organization-move-model';
import { OrganizationDefinitionModel } from '../../../organization-picker/model/organization-definition-model';


@Injectable({
    providedIn: 'root',
})
export class PerObjectDefinitionService {
    endpoint: string;
    readonly serviceRootPattern = '/objectDefinitionService/objectDefinition/';

    constructor(
        private httpClient: HttpClient,
        private configService: HrConfigurationService,
        private uiToastMessageService: UiToastMessageService,
        private uiTranslateLoaderService: UiTranslateLoaderService
    ) {
        this.endpoint = configService.config.hr.personnel;
    }

    public deleteById(id: number): Observable<any> {
        const pattern = this.serviceRootPattern + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public queryPage(pagination: PerObjectDefinitionQueryModel): Observable<HvlPage<Array<PerObjectDefinitionModel>>> {
        const pattern = this.serviceRootPattern + 'page';
        return this.httpClient
            .post<HvlResponse<HvlPage<PerObjectDefinitionModel[]>>>(this.endpoint + pattern, pagination)
            .pipe(
                map((hvlpage) => {
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<PerObjectDefinitionModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }

                    const converter = new JsonConvert();
                    const model = converter.deserializeArray(hvlpage.body.data, PerObjectDefinitionModel);
                    return new HvlPage<Array<PerObjectDefinitionModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public update(model: PerObjectDefinitionModel): Observable<PerObjectDefinitionModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerObjectDefinitionModel>>(
                this.endpoint + pattern,
                converter.serialize(model, PerObjectDefinitionModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerObjectDefinitionModel);
                }),
            );
    }

    public save(perObjectDefinitionModel: PerObjectDefinitionModel): Observable<PerObjectDefinitionModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerObjectDefinitionModel>>(
                this.endpoint + pattern,
                converter.serialize(perObjectDefinitionModel, PerObjectDefinitionModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerObjectDefinitionModel);
                }),
            );
    }

    public queryList(queryModel: PerObjectDefinitionQueryModel): Observable<Array<PerObjectDefinitionModel>> {
        const pattern = this.serviceRootPattern + 'query';
        return this.httpClient
            .post<HvlResponse<Array<PerObjectDefinitionModel>>>(this.endpoint + pattern, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerObjectDefinitionModel);
                }),
            );
    }

    public getList(): Observable<Array<PerObjectDefinitionModel>> {
        const url = `${this.endpoint}${this.serviceRootPattern}`;
        return this.httpClient.get<HvlResponse<Array<PerObjectDefinitionModel>>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerObjectDefinitionModel);
            }),
        );
    }

    public getById(id: number): Observable<PerObjectDefinitionModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}${id}`;
        return this.httpClient.get<HvlResponse<PerObjectDefinitionModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerObjectDefinitionModel);
            }),
        );
    }
    public enrichById(id: number): Observable<OrganizationDefinitionModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}enrichById/${id}`;
        return this.httpClient.get<HvlResponse<OrganizationDefinitionModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, OrganizationDefinitionModel);
            }),
        );
    }
    public getGenericObjNumber(param: string): Observable<string> {
        const url = `${this.endpoint}${this.serviceRootPattern}getGenericObjNumber/${param}`;
        return this.httpClient.get<HvlResponse<string>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return data.body || null;
            }),
        );
    }

    public lookupQuery(queryModel: PerObjectDefinitionQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookup/query';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitionQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupSubTree(queryModel: PerObjectDefinitionQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupSubTree/query';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitionQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupQueryWithCode(queryModel: PerObjectDefinitionQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryWithCode';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitionQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupQueryOfTitle(queryModel: PerObjectDefinitionQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryOfTitle';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitionQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupQueryOfTitleWithCode(queryModel: PerObjectDefinitionQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryOfTitleWithCode';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitionQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public getOrgHrmPerObjectDefOptionsForOrganizationSearch(
        searchText: string,
        enumObjects: EnumObject[],
        objectStatusCodeList: ObjectStatusConstraints[],
        activateHrAuthCheck: boolean,
        subTree: boolean,
    ): Observable<Array<LookupModel>> {
        if (!searchText) {
            return EMPTY;
        }
        const now = new Date();
        const perObjectDefinitionQueryModel: PerObjectDefinitionQueryModel = new PerObjectDefinitionQueryModel();
        perObjectDefinitionQueryModel.commonSearchText = searchText;
        perObjectDefinitionQueryModel.objectTypeCodes = enumObjects;
        perObjectDefinitionQueryModel.objectStatusCodeList = objectStatusCodeList;
        perObjectDefinitionQueryModel.beginDate = now;
        perObjectDefinitionQueryModel.endDate = now;
        perObjectDefinitionQueryModel.reverseBeginDateEndDateCheck = true;
        if (activateHrAuthCheck) perObjectDefinitionQueryModel.activateHrAuthCheck = true;
        if (subTree === false) {
            return this.lookupQuery(perObjectDefinitionQueryModel).pipe(
                map((resp) =>
                    resp.sort((a, b) => {
                        let fa = a.text.toLowerCase(),
                            fb = b.text.toLowerCase();
                        if (fa < fb) {
                            return -1;
                        }
                        if (fa > fb) {
                            return 1;
                        }
                        return 0;
                    }),
                ),
            );
        }

        return this.lookupSubTree(perObjectDefinitionQueryModel).pipe(
            map((resp) =>
                resp.sort((a, b) => {
                    let fa = a.text.toLowerCase(),
                        fb = b.text.toLowerCase();
                    if (fa < fb) {
                        return -1;
                    }
                    if (fa > fb) {
                        return 1;
                    }
                    return 0;
                }),
            ),
        );
    }


    public lookupQueryWithDatesOfOrganization(
        queryModel: PerObjectDefinitonQueryWithOrganizationDatesModel,
    ): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryWithDatesOfOrganization';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitonQueryWithOrganizationDatesModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupQueryWithDatesOfOrganizationPosition(
        queryModel: PerObjectDefinitonQueryWithOrganizationDatesModel,
    ): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryWithDatesOfOrganizationPosition';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitonQueryWithOrganizationDatesModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupQueryWithDatesOfOrganizationCadre(
        queryModel: PerObjectDefinitonQueryWithOrganizationDatesModel,
    ): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupQueryWithDatesOfOrganizationCadre';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, PerObjectDefinitonQueryWithOrganizationDatesModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public getPositionListForSelectedOrg(
        queryModel: ObjectHierarchyQueryModel,
    ): Observable<Array<PositionLookupModel>> {
        const pattern = this.serviceRootPattern + 'getPositionListForSelectedOrg';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PositionLookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, ObjectHierarchyQueryModel),
            )
            .pipe(
                map((data) => {
                    data.body?.forEach((pos) => {
                        pos.longText =
                            pos?.objectCode +
                            ' - ' +
                            pos?.longText +
                            (pos?.degreeOfCadre ? ' - ' + pos.degreeOfCadre : '');
                    });
                    return converter.deserializeArray(data.body || [], PositionLookupModel);
                }),
            );
    }

    public getPositionListForSelectedOrgForSecondment(
        queryModel: ObjectHierarchyQueryModel,
    ): Observable<Array<PositionLookupModel>> {
        const pattern = this.serviceRootPattern + 'getPositionListForSelectedOrgForSecondment';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PositionLookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, ObjectHierarchyQueryModel),
            )
            .pipe(
                map((data) => {
                    if (data.body[0]?.errorStatus == true) {
                        this.uiToastMessageService.showWarn(this.uiTranslateLoaderService.instant('POSITION_DOES_NOT_EXIST_FOR_SELECTED_ORGANIZATION_EXCEPTION'))
                        return []
                    }
                    data.body?.forEach((pos) => {
                        pos.longText =
                            pos?.objectCode +
                            ' - ' +
                            pos?.longText +
                            ' - ' +
                            (pos?.degreeOfCadre ? pos.degreeOfCadre : '');
                    });
                    return converter.deserializeArray(data.body || [], PositionLookupModel);
                }),
            );
    }

    public getPositionListForSelectedOrgForOrganizationEmployee(
        queryModel: ObjectHierarchyQueryModel,
    ): Observable<Array<PositionLookupModel>> {
        const pattern = this.serviceRootPattern + 'getPositionListForSelectedOrgForOrganizationEmployee';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<PositionLookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, ObjectHierarchyQueryModel),
            )
            .pipe(
                map((data) => {
                    if (data.body[0]?.errorStatus == true) {
                        this.uiToastMessageService.showWarn(this.uiTranslateLoaderService.instant('POSITION_DOES_NOT_EXIST_FOR_SELECTED_ORGANIZATION_EXCEPTION'))
                        return []
                    }
                    data.body?.forEach((pos) => {
                        pos.longText =
                            pos?.objectCode +
                            ' - ' +
                            pos?.longText +
                            ' - ' +
                            (pos?.degreeOfCadre ? pos.degreeOfCadre : '');
                    });
                    return converter.deserializeArray(data.body || [], PositionLookupModel);
                }),
            );
    }

    public getCadreListForSelectedPos(queryModel: ObjectHierarchyQueryModel): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'getCadreListForSelectedPos';
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<Array<LookupModel>>>(
                this.endpoint + pattern,
                converter.serialize(queryModel, ObjectHierarchyQueryModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeArray(data.body || [], LookupModel);
                }),
            );
    }

    public lookupSeparatedOrganizationUnitList(locale: string): Observable<Array<LookupModel>> {
        const pattern = this.serviceRootPattern + 'lookupSeparatedOrganizationUnitList/' + locale;
        return this.httpClient.get<HvlResponse<Array<LookupModel>>>(this.endpoint + pattern).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }

    public getDefinitionSchemaPersonnel(): Observable<Array<OrganizationSchemaModel>> {
        const pattern = this.serviceRootPattern + 'querySchemaPersonnelList';
        return this.httpClient.post<HvlResponse<Array<OrganizationSchemaModel>>>(this.endpoint + pattern, null).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], OrganizationSchemaModel);
            }),
        );
    }

    public moveOrganization(model: OrganizationMoveModel): Observable<any> {
        const pattern = this.serviceRootPattern + 'move';
        const converter = new JsonConvert();
        return this.httpClient.post<HvlResponse<any>>(this.endpoint + pattern, converter.serialize(model, null)).pipe(
            map((data) => {
                return data.body || null;
            }),
        );
    }

    public getTitleList(): Observable<Array<any>> {
        let perObjectDefinitionQueryModel = {objectTypeCode: "TITLE"}
        const pattern = this.serviceRootPattern + 'query';
        return this.httpClient
            .post<HvlResponse<Array<any>>>(this.endpoint + pattern, perObjectDefinitionQueryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return data.body || [];
                }),
            );
    }
}
